import axios from 'axios';

// Use same domain in production (avoids CORS), env var in development
const BACKEND_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin 
  : process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  adminLogin: (data) => api.post('/admin/login', data),
  adminVerify2FA: (data) => api.post('/admin/verify-2fa', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Products APIs
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  // Filter endpoints
  getBrands: (category) => api.get('/products/filters/brands', { params: { category } }),
  getGrades: (category) => api.get('/products/filters/grades', { params: { category } }),
  getStorages: (category) => api.get('/products/filters/storage', { params: { category } }),
  getSpecifications: (category, minCount) => api.get('/products/filters/specifications', { params: { category, min_count: minCount } }),
  getAvailabilityStats: (category) => api.get('/products/filters/availability', { params: { category } }),
  getCompatibility: (category, compatBrand) => api.get('/products/filters/compatibility', { params: { category, compat_brand: compatBrand || undefined } }),
};

// Categories APIs
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Cart APIs
export const cartAPI = {
  get: () => api.get('/cart'),
  addItem: (data) => api.post('/cart/items', data),
  updateItem: (productId, data, stockId) => api.put(`/cart/items/${productId}`, data, { params: stockId ? { stockId } : {} }),
  removeItem: (productId, stockId) => api.delete(`/cart/items/${productId}`, { params: stockId ? { stockId } : {} }),
  clear: () => api.delete('/cart'),
};

// Wishlist APIs
export const wishlistAPI = {
  get: () => api.get('/wishlist'),
  add: (data) => api.post('/wishlist', data),
  remove: (productId) => api.delete(`/wishlist/${productId}`),
};

// Orders APIs
export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
};

// Reviews APIs
export const reviewsAPI = {
  getByProduct: (productId) => api.get(`/products/${productId}/reviews`),
  create: (productId, data) => api.post(`/products/${productId}/reviews`, data),
  update: (reviewId, data) => api.put(`/products/reviews/${reviewId}`, data),
  delete: (reviewId) => api.delete(`/products/reviews/${reviewId}`),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get('/admin/suppliers'),
  getById: (id) => api.get(`/admin/suppliers/${id}`),
  create: (data) => api.post('/admin/suppliers', data),
  update: (id, data) => api.put(`/admin/suppliers/${id}`, data),
  delete: (id) => api.delete(`/admin/suppliers/${id}`),
  // Shipping data for AWB
  getShippingData: (id) => api.get(`/admin/suppliers/${id}/shipping-data`),
  updateShippingData: (id, data) => api.put(`/admin/suppliers/${id}/shipping-data`, data),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrdersSuppliers: () => api.get('/admin/orders/suppliers'),
  updateOrderStatus: (orderId, data) => api.put(`/admin/orders/${orderId}/status`, data),
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getReviews: (params) => api.get('/admin/reviews', { params }),
  createFanCourierAWB: (orderId) => api.post('/admin/fan-courier/create-awb', { order_id: orderId }),
  trackFanCourier: (awb) => api.post('/admin/fan-courier/tracking', { awb }),
  // Sameday AWB
  createSamedayAWB: (orderId) => api.post('/admin/sameday/create-awb', { order_id: orderId }),
  trackSameday: (awb) => api.post('/admin/sameday/tracking', { awb }),
  downloadSamedayPDF: (awb, format = 'A4') => api.get(`/admin/sameday/awb/${awb}/pdf?format=${format}`, { responseType: 'blob' }),
  // Delete AWB
  deleteSamedayAWB: (orderId, awb) => api.post('/admin/sameday/delete-awb', { order_id: orderId, awb }),
  // Colete Online AWB (global - auto-resolves the enabled supplier)
  createColeteOnlineAWB: (orderId) => api.post('/admin/coleteonline/create-awb', { order_id: orderId }),
  coleteOnlinePrice: (orderId) => api.post('/admin/coleteonline/price', { order_id: orderId }),
  getEffectiveTransportDefault: () => api.get('/admin/coleteonline/effective-default'),
  trackColeteOnline: (uniqueId) => api.post('/admin/coleteonline/tracking', { uniqueId }),
  downloadColeteOnlinePDF: (uniqueId) => api.get(`/admin/coleteonline/awb/${uniqueId}/pdf`, { responseType: 'blob' }),
  // Supplier Orders Migration
  migrateSupplierOrders: () => api.post('/admin/migrate-supplier-orders'),
};

export const paymentsAPI = {
  list: (params) => api.get('/admin/payments', { params }),
  summary: () => api.get('/admin/payments/summary'),
  status: (orderId) => api.get(`/admin/payments/ipay/status/${orderId}`),
  deposit: (data) => api.post('/admin/payments/ipay/deposit', data),
  refund: (data) => api.post('/admin/payments/ipay/refund', data),
  reverse: (data) => api.post('/admin/payments/ipay/reverse', data),
  logs: (orderId, limit = 50) => api.get('/admin/payments/ipay/logs', { params: { orderId, limit } }),
  diagnose: (orderId) => api.get(`/admin/payments/ipay/diagnose/${orderId}`),
  payLink: (data) => api.post('/admin/payments/ipay/pay-link', data),
};

export default api;
